import type { RouterOutputs } from "@gately/api";
import type { ColumnDef, Row } from "@gately/ui/data-table";
import type { PassStatus, Terms } from "@gately/validators";
import { useMemo, useState } from "react";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronUp,
  ClipboardPen,
  Copy,
  MessageCircle,
  Plus,
  Send,
  X,
} from "lucide-react";

import { cn } from "@gately/ui";
import { Avatar } from "@gately/ui/avatar";
import { Button } from "@gately/ui/button";
import { DataTable, SortableHeader } from "@gately/ui/data-table";
import { toast } from "@gately/ui/toast";
import {
  formatRecurringSchedule,
  formatVisitInstant,
  formatVisitRange,
  isLiveGuestStatus,
  viewerTimeZone,
} from "@gately/validators";

import type { SubTabKey } from "~/lib/invite-status";
import {
  deriveGuestRowStatus,
  deriveInviteSummaryStatus,
  hostGuestName,
  incompleteGuestCount,
  PASS_STATUS_LABELS,
  tabFor,
} from "~/lib/invite-status";
import type { CompletableGuest } from "~/components/invite/CompleteGuestFormDialog";
import { CompleteGuestFormDialog } from "~/components/invite/CompleteGuestFormDialog";
import { orpc } from "~/lib/orpc";
import { frenchErrorMessage } from "~/lib/orpc-error";
import { passUrl, whatsappShareUrl } from "~/lib/pass-share";
import {
  canResendSelfFill,
  isStaleResendRefusal,
  resendSelfFillErrorMessage,
  resendTargetGuestId,
} from "~/lib/self-fill-resend";
import { useOrgTimeZone } from "~/lib/use-me";
import { resolvePermissions, usePermissions } from "~/lib/use-permissions";
import { useTerms } from "~/lib/use-terms";

type InviteGuest = RouterOutputs["invite"]["list"][number]["guests"][number];

// Resolved once per module, not per render.
const VIEWER = viewerTimeZone();

export const Route = createFileRoute("/_dashboard/invitations")({
  // Manager-only, like every page in the management nav: the nav filter
  // hid it, but the URL was always reachable.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).canManage) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  // Prefetch in the loader so the component's useSuspenseQuery has data ready
  // and never suspends mid-navigation (which blanks/remounts the whole tree).
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(
      orpc.invite.list.queryOptions({ input: { limit: 100 } }),
    ),
  component: VisiteursPage,
});

// ─── French labels ────────────────────────────────────────────────────────────
// `date_range` reads "Séjour" in a residence and "Multiple" in an office
// (Terms.dateRangeLabel); the other two are the same in both verticals.
function typeLabels(terms: Terms): Record<string, string> {
  return {
    one_time: "Unique",
    date_range: terms.dateRangeLabel,
    recurring: "Récurrent",
  };
}

// ─── Derived status ───────────────────────────────────────────────────────────
// Aliased to the shared PassStatus (not a local 4-value union) so this list
// agrees with /attendus and the pass page on every status, including
// "off_schedule" for a recurring pass outside its weekday/hour window. The
// actual derivation (collapsed-row summary + per-guest) lives in
// ~/lib/invite-status so it's node-unit-testable.
type DerivedStatus = PassStatus;

// ─── Validity label ───────────────────────────────────────────────────────────
// validFrom/validTo are stored instants whose time-of-day is authored and
// evaluated in the ORGANIZATION's zone (GATELY-40) — display must agree, so
// every date/time render below goes through the shared zoned formatter
// rather than a per-file copy pinned to a hand-picked zone.

// Recurring's overall span is calendar-day bounds only (00:00/23:59
// placeholders — see buildValidity), never a real clock reading, so there is
// no instant here for `formatVisitInstant` to label a zone on.
function dateOnly(d: string | Date, timeZone: string): string {
  return new Date(d).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    timeZone,
  });
}

function validityLabel(
  it: {
    type: string;
    validFrom: string | Date | null;
    validTo: string | Date | null;
    recurrence?: string | null;
    dailyStartMinutes: number | null;
    dailyEndMinutes: number | null;
  },
  timeZone: string,
): { text: string; secondary: string | null } {
  // Unique/Séjour carry a meaningful time-of-day window — show it so the
  // selected hours are verifiable. Recurring keeps a date-only span here (its
  // daily window is appended separately below via formatRecurringSchedule).
  const withTime = it.type === "one_time" || it.type === "date_range";
  let text = "—";
  let secondary: string | null = null;
  if (it.validFrom && it.validTo) {
    if (withTime) {
      // One call for both ends — composing this from two independent
      // `formatVisitInstant` calls would print the zone label twice.
      const range = formatVisitRange(it.validFrom, it.validTo, timeZone, {
        viewerTimeZone: VIEWER,
        withDate: true,
      });
      text = range.primary;
      secondary = range.secondary;
    } else {
      text = `${dateOnly(it.validFrom, timeZone)} – ${dateOnly(it.validTo, timeZone)}`;
    }
  } else if (it.validFrom) {
    if (withTime) {
      const from = formatVisitInstant(it.validFrom, timeZone, {
        viewerTimeZone: VIEWER,
        withDate: true,
      });
      text = `À partir du ${from.primary}`;
      secondary = from.secondary;
    } else {
      text = `À partir du ${dateOnly(it.validFrom, timeZone)}`;
    }
  }
  if (it.type !== "recurring") return { text, secondary };
  // Daily weekday/hours pattern, e.g. "Lun, Mer, Ven · 08:00–18:00 · heure de
  // Casablanca" — "" when the recurrence is unparseable, in which case the
  // date span above stands alone.
  const schedule = formatRecurringSchedule(
    {
      recurrence: it.recurrence ?? null,
      dailyStartMinutes: it.dailyStartMinutes,
      dailyEndMinutes: it.dailyEndMinutes,
    },
    timeZone,
  );
  return { text: schedule ? `${text} · ${schedule}` : text, secondary };
}

// ─── Type pill ────────────────────────────────────────────────────────────────
function TypePill({ type }: { type: string }) {
  const terms = useTerms();
  const label = typeLabels(terms)[type] ?? type;
  const cls =
    type === "recurring"
      ? "bg-info-tint text-info"
      : "bg-muted text-muted-foreground";
  return (
    <span
      className={cn(
        "inline-flex h-[22px] items-center rounded-full px-2.5 text-xs font-medium",
        cls,
      )}
    >
      {label}
    </span>
  );
}

// ─── Status pill ─────────────────────────────────────────────────────────────
function StatusPill({ status }: { status: DerivedStatus }) {
  // Labels live in ~/lib/invite-status so the /repertoire visitor sheet names
  // each status identically; only the pill's colours are this screen's.
  const map: Record<DerivedStatus, { cls: string; dot: string }> = {
    active: { cls: "bg-success-tint text-success", dot: "bg-success" },
    upcoming: { cls: "bg-info-tint text-info", dot: "bg-info" },
    expired: {
      cls: "bg-muted text-muted-foreground",
      dot: "bg-muted-foreground",
    },
    revoked: {
      cls: "bg-(--danger-50) text-(--danger-500)",
      dot: "bg-(--danger-500)",
    },
    used: {
      cls: "bg-muted text-muted-foreground",
      dot: "bg-muted-foreground",
    },
    pending: { cls: "bg-warning-tint text-warning", dot: "bg-warning" },
    rejected: {
      cls: "bg-(--danger-50) text-(--danger-500)",
      dot: "bg-(--danger-500)",
    },
    off_schedule: { cls: "bg-warning-tint text-warning", dot: "bg-warning" },
    incomplete: { cls: "bg-warning-tint text-warning", dot: "bg-warning" },
  };
  const { cls, dot } = map[status];
  const label = PASS_STATUS_LABELS[status];
  return (
    <span
      className={cn(
        "inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-[12.5px] font-medium",
        cls,
      )}
    >
      <span className={cn("size-1.5 rounded-full", dot)} />
      {label}
    </span>
  );
}

// ─── Status bucket ────────────────────────────────────────────────────────────
// `tabFor` folds the eight raw statuses into three buckets and returns null for
// revoked, which the old tab strip could only surface under "Tous". As a
// filter chip revoked gets its own selectable bucket.
type Bucket = SubTabKey | "revoked";

const BUCKET_OPTIONS: { value: Bucket; label: string }[] = [
  { value: "active", label: "Actifs" },
  { value: "upcoming", label: "À venir" },
  { value: "expired", label: "Expirés" },
  { value: "revoked", label: "Révoqués" },
];

const bucketOf = (status: DerivedStatus): Bucket => tabFor(status) ?? "revoked";

// ─── Expanded per-guest row ───────────────────────────────────────────────────
function GuestRow({
  guest,
  status,
  onRevoke,
  onResend,
  resending,
  onComplete,
}: {
  guest: InviteGuest;
  status: DerivedStatus;
  onRevoke: () => void;
  onResend: () => void;
  resending: boolean;
  onComplete: () => void;
}) {
  // The host's own guest: an unnamed self-fill visitor shows by email
  // (hostGuestName — plain, never the masked desk/reception copy).
  const label = hostGuestName(guest);
  return (
    <div className="bg-card flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
      <div className="flex min-w-0 items-center gap-2.5">
        <Avatar
          name={label}
          className="size-7 shrink-0 text-[11px]"
        />
        <div className="min-w-0">
          <div className="text-foreground truncate text-[13px] font-medium">
            {label}
          </div>
          <div className="text-muted-foreground font-mono text-[11px]">
            {guest.code}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <StatusPill status={status} />
        <button
          type="button"
          aria-label="Copier le lien"
          onClick={() => {
            void navigator.clipboard
              .writeText(passUrl(guest.token))
              .then(() => toast.success("Lien copié."));
          }}
          className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-8 items-center justify-center rounded-lg transition-colors"
        >
          <Copy size={14} strokeWidth={1.85} />
        </button>
        {guest.guestPhone ? (
          <a
            href={whatsappShareUrl(guest.token, label)}
            target="_blank"
            rel="noreferrer"
            aria-label="Partager via WhatsApp"
            className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-8 items-center justify-center rounded-lg transition-colors"
          >
            <MessageCircle size={14} strokeWidth={1.85} />
          </a>
        ) : null}
        {canResendSelfFill(status) ? (
          <button
            type="button"
            aria-label="Renvoyer le lien"
            title="Renvoyer le lien"
            disabled={resending}
            onClick={onResend}
            className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-8 items-center justify-center rounded-lg transition-colors disabled:opacity-40"
          >
            <Send size={14} strokeWidth={1.85} />
          </button>
        ) : null}
        {canResendSelfFill(status) ? (
          <button
            type="button"
            aria-label="Remplir le formulaire à sa place"
            title="Remplir le formulaire à sa place"
            onClick={onComplete}
            className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-8 items-center justify-center rounded-lg transition-colors"
          >
            <ClipboardPen size={14} strokeWidth={1.85} />
          </button>
        ) : null}
        {isLiveGuestStatus(guest.status) ? (
          <button
            type="button"
            aria-label="Révoquer cet invité"
            onClick={onRevoke}
            className="text-muted-foreground flex size-8 items-center justify-center rounded-lg transition-colors hover:bg-(--danger-50) hover:text-(--danger-500)"
          >
            <X size={14} strokeWidth={1.85} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
function VisiteursPage() {
  const queryClient = useQueryClient();
  const { data: invites } = useSuspenseQuery(
    orpc.invite.list.queryOptions({ input: { limit: 100 } }),
  );
  const terms = useTerms();
  const timeZone = useOrgTimeZone();
  const perms = usePermissions();

  const [q, setQ] = useState("");

  const revoke = useMutation(
    orpc.invite.revoke.mutationOptions({
      onSuccess: async () => {
        toast.success("Invitation révoquée.");
        await queryClient.invalidateQueries({
          queryKey: orpc.invite.list.key(),
        });
      },
      onError: (err) => toast.error(err.message || "Échec de la révocation."),
    }),
  );

  const revokeGuest = useMutation(
    orpc.invite.revokeGuest.mutationOptions({
      onSuccess: async () => {
        toast.success("Invité révoqué.");
        await queryClient.invalidateQueries({
          queryKey: orpc.invite.list.key(),
        });
      },
      onError: (err) => toast.error(err.message || "Échec de la révocation."),
    }),
  );

  const resend = useMutation(
    orpc.invite.resendSelfFill.mutationOptions({
      onSuccess: async () => {
        toast.success("Lien renvoyé.");
        await queryClient.invalidateQueries({
          queryKey: orpc.invite.list.key(),
        });
      },
      onError: (err) => {
        toast.error(
          resendSelfFillErrorMessage(
            err,
            frenchErrorMessage(err, "Impossible de renvoyer le lien."),
          ),
        );
        // Submitted, revoked or expired since the list loaded: refetch so
        // the stale « Renvoyer le lien » goes away.
        if (isStaleResendRefusal(err)) {
          void queryClient.invalidateQueries({
            queryKey: orpc.invite.list.key(),
          });
        }
      },
    }),
  );
  const { mutate: resendMutate, isPending: resendPending } = resend;
  const [completing, setCompleting] = useState<CompletableGuest | null>(null);

  // ── Compute derived status for each invite ──
  const now = new Date();
  const withStatus = useMemo(
    () =>
      invites.map((it) => ({
        ...it,
        _status: deriveInviteSummaryStatus(it, timeZone, now),
        _incomplete: incompleteGuestCount(it, timeZone, now),
        _name: hostGuestName(
          it.guests[0] ?? { guestName: it.guestName, guestEmail: null },
        ),
      })),
    // `now` is re-read on every render on purpose (status is time-dependent),
    // so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invites, timeZone],
  );

  type InviteRow = (typeof withStatus)[number];

  const { mutate: revokeMutate } = revoke;
  const { mutate: revokeGuestMutate } = revokeGuest;

  // Memoised: flexRender treats each `cell` as a component type, so a fresh
  // array every render would remount the cells and lose an expanded row.
  const columns: ColumnDef<InviteRow>[] = useMemo(
    () => [
      {
        id: "guest",
        accessorFn: (it) =>
          `${it._name} ${it.guests.map((g) => g.guestEmail ?? "").join(" ")} ${it.code}`,
        size: 260,
        minSize: 200,
        enableHiding: false,
        meta: { label: "Invité" },
        header: ({ column }) => (
          <SortableHeader column={column}>Invité</SortableHeader>
        ),
        cell: ({ row }) => {
          const it = row.original;
          return (
            <div className="flex min-w-0 items-center gap-2.5">
              <Avatar
                name={it._name}
                className="size-[34px] shrink-0 text-[12px]"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-foreground truncate text-[13.5px] font-semibold">
                    {it._name}
                  </span>
                  {it.source === "email" ? (
                    <span className="text-muted-foreground ml-2 rounded border px-1.5 py-0.5 align-middle text-[11px]">
                      par e-mail
                    </span>
                  ) : null}
                  {it.guestCount > 1 ? (
                    <span className="bg-muted text-muted-foreground shrink-0 rounded-full px-1.5 py-0.5 text-[10.5px] font-medium whitespace-nowrap">
                      👥 {it.guestCount}
                    </span>
                  ) : null}
                  {it.guestCount > 1 && it._incomplete > 0 ? (
                    <span className="bg-warning-tint text-warning shrink-0 rounded-full px-1.5 py-0.5 text-[10.5px] font-medium whitespace-nowrap">
                      {it._incomplete} en attente du visiteur
                    </span>
                  ) : null}
                </div>
                <div className="text-muted-foreground font-mono text-[11.5px]">
                  {it.code}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "type",
        size: 130,
        minSize: 110,
        enableGlobalFilter: false,
        filterFn: "arrIncludesSome",
        meta: { label: "Type" },
        header: ({ column }) => (
          <SortableHeader column={column}>Type</SortableHeader>
        ),
        cell: ({ getValue }) => <TypePill type={getValue<string>()} />,
      },
      {
        id: "host",
        accessorFn: (it) => it.createdByName ?? "",
        size: 200,
        minSize: 150,
        meta: {
          label: terms.hostUnitLabel,
          filter: { kind: "text", placeholder: "Filtrer…" },
        },
        header: ({ column }) => (
          <SortableHeader column={column}>{terms.hostUnitLabel}</SortableHeader>
        ),
        cell: ({ row }) => {
          const it = row.original;
          const villaLabel = it.number
            ? it.building
              ? `${it.building} ${it.number}`
              : it.number
            : terms.orgLabel;
          return (
            <div className="min-w-0">
              <div className="text-foreground truncate text-[13px]">
                {it.createdByName ?? "—"}
              </div>
              <div className="text-muted-foreground text-[12px]">
                {villaLabel}
              </div>
            </div>
          );
        },
      },
      {
        id: "validity",
        accessorFn: (it) => it.validFrom ?? "",
        size: 230,
        minSize: 170,
        enableGlobalFilter: false,
        meta: { label: "Validité" },
        header: ({ column }) => (
          <SortableHeader column={column}>Validité</SortableHeader>
        ),
        cell: ({ row }) => {
          const { text, secondary } = validityLabel(row.original, timeZone);
          return (
            <span
              className="text-muted-foreground font-mono text-[12px]"
              title={secondary ?? undefined}
            >
              {text}
            </span>
          );
        },
      },
      {
        id: "status",
        accessorFn: (it) => bucketOf(it._status),
        size: 190,
        minSize: 150,
        enableGlobalFilter: false,
        filterFn: "arrIncludesSome",
        meta: { label: "Statut" },
        header: ({ column }) => (
          <SortableHeader column={column}>Statut</SortableHeader>
        ),
        cell: ({ row }) => <StatusPill status={row.original._status} />,
      },
      {
        id: "actions",
        size: 140,
        minSize: 130,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        enableGlobalFilter: false,
        meta: { label: "Actions", align: "right" },
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          const it = row.original;
          return (
            <div className="flex items-center justify-end gap-1">
              <button
                type="button"
                aria-label="Copier le lien"
                onClick={() => {
                  void navigator.clipboard
                    .writeText(passUrl(it.token))
                    .then(() => toast.success("Lien copié."));
                }}
                className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-8 items-center justify-center rounded-lg transition-colors"
              >
                <Copy size={15} strokeWidth={1.85} />
              </button>
              <a
                href={whatsappShareUrl(it.token, it._name)}
                target="_blank"
                rel="noreferrer"
                aria-label="Partager via WhatsApp"
                className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-8 items-center justify-center rounded-lg transition-colors"
              >
                <MessageCircle size={15} strokeWidth={1.85} />
              </a>
              {(() => {
                const guestId = resendTargetGuestId(it, it._status);
                return guestId ? (
                  <button
                    type="button"
                    aria-label="Renvoyer le lien"
                    title="Renvoyer le lien"
                    disabled={resendPending}
                    onClick={() => resendMutate({ guestId })}
                    className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-8 items-center justify-center rounded-lg transition-colors disabled:opacity-40"
                  >
                    <Send size={15} strokeWidth={1.85} />
                  </button>
                ) : null;
              })()}
              {(() => {
                const guestId = resendTargetGuestId(it, it._status);
                const g = it.guests[0];
                return guestId && g ? (
                  <button
                    type="button"
                    aria-label="Remplir le formulaire à sa place"
                    title="Remplir le formulaire à sa place"
                    onClick={() =>
                      setCompleting({
                        id: g.id,
                        guestName: g.guestName,
                        guestEmail: g.guestEmail,
                        visitorType: it.visitorType,
                      })
                    }
                    className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-8 items-center justify-center rounded-lg transition-colors"
                  >
                    <ClipboardPen size={15} strokeWidth={1.85} />
                  </button>
                ) : null;
              })()}
              {it.status !== "revoked" ? (
                <button
                  type="button"
                  aria-label="Révoquer"
                  onClick={() => revokeMutate({ id: it.id })}
                  className="text-muted-foreground flex size-8 items-center justify-center rounded-lg transition-colors hover:bg-(--danger-50) hover:text-(--danger-500)"
                >
                  <X size={15} strokeWidth={1.85} />
                </button>
              ) : null}
              {row.getCanExpand() ? (
                <button
                  type="button"
                  aria-label={
                    row.getIsExpanded()
                      ? "Masquer les invités"
                      : "Voir les invités"
                  }
                  onClick={() => row.toggleExpanded()}
                  className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-8 items-center justify-center rounded-lg transition-colors"
                >
                  {row.getIsExpanded() ? (
                    <ChevronUp size={15} strokeWidth={1.85} />
                  ) : (
                    <ChevronDown size={15} strokeWidth={1.85} />
                  )}
                </button>
              ) : null}
            </div>
          );
        },
      },
    ],
    [terms, revokeMutate, timeZone, resendMutate, resendPending],
  );

  const renderSubRow = (row: Row<InviteRow>) => (
    <div className="flex flex-col gap-2 px-5 py-3">
      {row.original.guests.map((g) => (
        <GuestRow
          key={g.id}
          guest={g}
          status={deriveGuestRowStatus(row.original, g, timeZone, now)}
          onRevoke={() => revokeGuestMutate({ id: g.id })}
          onResend={() => resendMutate({ guestId: g.id })}
          resending={resendPending}
          onComplete={() =>
            setCompleting({
              id: g.id,
              guestName: g.guestName,
              guestEmail: g.guestEmail,
              visitorType: row.original.visitorType,
            })
          }
        />
      ))}
    </div>
  );

  return (
    <div className="px-7 py-6">
      {/* Header */}
      <div className="mb-5 flex items-end justify-between">
        <div>
          <h1 className="text-foreground text-2xl font-semibold tracking-[-0.02em]">
            Invitations
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {invites.length} invitations sur {terms.thisOrg}
          </p>
        </div>
        {/* Hidden for anyone the form would dead-end: staff and gardiens
            hold no unit and no residence scope (GATELY-58, decision 1). */}
        {perms.canCreateInvite ? (
          // The import and paste-copilot paths now live inside /inviter
          // itself (mode switcher, spec §3 decision 3), so this is a plain
          // link for every org type - no more office-only split button.
          <Button asChild>
            <Link to="/inviter">
              <Plus className="size-4" />
              Nouvelle invitation
            </Link>
          </Button>
        ) : null}
      </div>

      <CompleteGuestFormDialog
        guest={completing}
        onOpenChange={(open) => {
          if (!open) setCompleting(null);
        }}
      />

      <DataTable
        columns={columns}
        data={withStatus}
        pageSize={25}
        initialPinning={{ left: ["guest"], right: ["actions"] }}
        showColumnFilters
        showColumnToggle
        search={{
          value: q,
          onChange: setQ,
          placeholder: "Rechercher un invité ou un code…",
        }}
        globalFilter={q}
        filters={[
          { columnId: "status", label: "Statut", options: BUCKET_OPTIONS },
          {
            columnId: "type",
            label: "Type",
            options: Object.entries(typeLabels(terms)).map(
              ([value, label]) => ({
                value,
                label,
              }),
            ),
          },
        ]}
        getRowCanExpand={(row) => row.original.guestCount > 1}
        footer={
          <span>
            {withStatus.length} invitation
            {withStatus.length === 1 ? "" : "s"}
          </span>
        }
        renderSubRow={renderSubRow}
        empty={{ title: "Aucune invitation pour le moment." }}
      />
    </div>
  );
}
